import React, { useState } from 'react';
import { UserWithMetrics, userManagementService } from '../services/user-management-service';
import { X, Shield, UserX, RefreshCw } from 'lucide-react';

interface EditUserModalProps {
  user: UserWithMetrics;
  currentUserRole: 'owner' | 'manager' | 'user';
  onClose: () => void;
  onSuccess: () => void;
}

export function EditUserModal({ user, currentUserRole, onClose, onSuccess }: EditUserModalProps) {
  const [selectedRole, setSelectedRole] = useState<'user' | 'manager'>(
    user.role === 'owner' ? 'manager' : user.role
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmAction, setConfirmAction] = useState<'deactivate' | 'delete' | null>(null);

  const canEdit = currentUserRole === 'owner' || currentUserRole === 'manager';
  const canDelete = currentUserRole === 'owner';

  const handleUpdateRole = async () => {
    if (selectedRole === user.role) {
      onClose();
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await userManagementService.updateUserRole(user.id, selectedRole);
      
      if (!result.success) {
        setError(result.error || 'Erro ao atualizar role');
        setLoading(false);
        return;
      }

      onSuccess();
    } catch (err) {
      console.error('Erro ao atualizar role:', err);
      setError('Erro ao atualizar role. Tente novamente.');
      setLoading(false);
    }
  };

  const handleDeactivate = async () => {
    setLoading(true);
    setError('');

    try {
      const result = user.status === 'active' 
        ? await userManagementService.deactivateUser(user.id)
        : await userManagementService.reactivateUser(user.id);
      
      if (!result.success) {
        setError(result.error || 'Erro ao alterar status');
        setLoading(false);
        return;
      }

      onSuccess();
    } catch (err) {
      console.error('Erro ao alterar status:', err);
      setError('Erro ao alterar status. Tente novamente.');
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await userManagementService.deleteUser(user.id);
      
      if (!result.success) {
        setError(result.error || 'Erro ao remover usuário');
        setLoading(false);
        return;
      }

      onSuccess();
    } catch (err) {
      console.error('Erro ao remover usuário:', err);
      setError('Erro ao remover usuário. Tente novamente.');
      setLoading(false);
    }
  };

  if (confirmAction) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
              <UserX className="w-6 h-6 text-red-600" />
            </div>

            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
              {confirmAction === 'deactivate' 
                ? (user.status === 'active' ? 'Desativar Usuário?' : 'Reativar Usuário?')
                : 'Remover Usuário?'}
            </h3>

            <p className="text-sm text-gray-600 text-center mb-6">
              {confirmAction === 'deactivate' ? (
                user.status === 'active' ? (
                  <>O usuário <strong>{user.name}</strong> será desativado e perderá acesso à plataforma.</>
                ) : (
                  <>O usuário <strong>{user.name}</strong> será reativado e terá acesso à plataforma novamente.</>
                )
              ) : (
                <>O usuário <strong>{user.name}</strong> será removido permanentemente. Esta ação não pode ser desfeita.</>
              )}
            </p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
                {error}
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={() => setConfirmAction(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                onClick={confirmAction === 'deactivate' ? handleDeactivate : handleDelete}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                  confirmAction === 'delete'
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : user.status === 'active'
                    ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
                disabled={loading}
              >
                {loading ? 'Processando...' : (
                  confirmAction === 'deactivate' 
                    ? (user.status === 'active' ? 'Desativar' : 'Reativar')
                    : 'Remover'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Editar Usuário</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* User Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-medium flex-shrink-0">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="font-medium text-gray-900">{user.name}</div>
                <div className="text-sm text-gray-500">{user.email}</div>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm text-gray-600 mt-3 pt-3 border-t border-gray-200">
              <span>Status:</span>
              <span className={`font-medium ${user.status === 'active' ? 'text-green-600' : 'text-red-600'}`}>
                {user.status === 'active' ? 'Ativo' : 'Inativo'}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm text-gray-600 mt-2">
              <span>Interações (Mês):</span>
              <span className="font-medium">{user.monthlyInteractions}</span>
            </div>
          </div>

          {/* Role Selection */}
          {canEdit && user.role !== 'owner' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nível de Acesso
              </label>
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value as 'user' | 'manager')}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                  disabled={loading}
                >
                  <option value="user">Usuário - Acesso padrão</option>
                  <option value="manager">Administrador - Gestão completa</option>
                </select>
              </div>
            </div>
          )}

          {user.role === 'owner' && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-sm text-purple-800">
                <strong>Proprietário:</strong> Este usuário não pode ser editado ou removido.
              </p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3">
            {/* Update Role Button */}
            {canEdit && user.role !== 'owner' && selectedRole !== user.role && (
              <button
                onClick={handleUpdateRole}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Atualizando...' : 'Atualizar Nível de Acesso'}
              </button>
            )}

            {/* Deactivate/Reactivate Button */}
            {canEdit && user.role !== 'owner' && (
              <button
                onClick={() => setConfirmAction('deactivate')}
                className={`w-full px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2 ${
                  user.status === 'active'
                    ? 'bg-yellow-50 text-yellow-700 border border-yellow-300 hover:bg-yellow-100'
                    : 'bg-green-50 text-green-700 border border-green-300 hover:bg-green-100'
                }`}
                disabled={loading}
              >
                <RefreshCw className="w-4 h-4" />
                <span>{user.status === 'active' ? 'Desativar Usuário' : 'Reativar Usuário'}</span>
              </button>
            )}

            {/* Delete Button */}
            {canDelete && user.role !== 'owner' && (
              <button
                onClick={() => setConfirmAction('delete')}
                className="w-full px-4 py-2 bg-red-50 text-red-700 border border-red-300 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center space-x-2"
                disabled={loading}
              >
                <UserX className="w-4 h-4" />
                <span>Remover Usuário</span>
              </button>
            )}

            {/* Close Button */}
            <button
              onClick={onClose}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

